// Finds a route between two tram stops across the schematic Metrolink network.
//
// The map is drawn from manchester.json `lines` (each an ordered polyline of
// nodes, some named stations and some bend points). We model those as a graph
// and route with Dijkstra over (station, line) states, applying a large penalty
// on each line change so the result prioritises the fewest changes; distance is
// only used to break ties. The route is returned as a list of coloured
// segments (each a polyline in map units) that the map overlays on a
// greyed-out network.

const TRANSFER_PENALTY = 1e9; // huge, so fewer changes always wins

const normalize = (name) =>
  String(name)
    .replace(/[0-9]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();

// Some logical lines are split across multiple manchester.json lines (e.g. the
// purple line is "AltrinchamEtihadCampus" + "PiccadillyEtihadCampus"). Lines
// that share a colour and a station are the same logical line, so give them a
// shared group id and treat a "change" as switching group rather than file.
function assignGroups(lines) {
  const n = lines.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (x) => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  const union = (a, b) => {
    parent[find(a)] = find(b);
  };

  const sets = lines.map((l) => new Set(l.named.map((s) => s.name)));

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (lines[i].color !== lines[j].color) continue;
      const a = sets[i];
      const b = sets[j];
      let shared = false;
      for (const s of a) {
        if (b.has(s)) {
          shared = true;
          break;
        }
      }
      if (shared) union(i, j);
    }
  }

  return Array.from({ length: n }, (_, i) => find(i));
}

function buildGeometry(tramData) {
  const lines = tramData.lines.map((line) => {
    const points = line.nodes.map((node) => ({
      x: node.coords[0],
      y: node.coords[1],
      name: node.name ? normalize(node.name) : null,
    }));

    // Ordered named stations (deduped by name) with their point index.
    const named = [];
    const seen = {};
    points.forEach((p, i) => {
      if (p.name && !seen[p.name]) {
        seen[p.name] = true;
        named.push({ name: p.name, idx: i });
      }
    });

    return { name: line.name, color: line.color, points, named };
  });

  const groups = assignGroups(lines);
  lines.forEach((line, li) => {
    line.group = groups[li];
  });

  // Station adjacency across all lines (interchanges connect lines).
  const adjacency = {};
  const addEdge = (from, to, lineIdx, fromIdx, toIdx, dist, group) => {
    (adjacency[from] || (adjacency[from] = [])).push({ to, lineIdx, fromIdx, toIdx, dist, group });
  };

  lines.forEach((line, li) => {
    for (let i = 0; i + 1 < line.named.length; i++) {
      const a = line.named[i];
      const b = line.named[i + 1];
      const dist = Math.hypot(
        line.points[b.idx].x - line.points[a.idx].x,
        line.points[b.idx].y - line.points[a.idx].y
      );
      addEdge(a.name, b.name, li, a.idx, b.idx, dist, line.group);
      addEdge(b.name, a.name, li, b.idx, a.idx, dist, line.group);
    }
  });

  return { lines, adjacency };
}

// Dijkstra over (station, incomingLineGroup) states. Switching group costs
// TRANSFER_PENALTY, so the route prefers the fewest line changes.
function dijkstra(adjacency, start, goal) {
  const keyOf = (station, group) => `${station}|${group}`;

  const dist = {};
  const prev = {};
  const settled = new Set();

  const startKey = keyOf(start, -1);
  dist[startKey] = 0;
  const queue = [startKey];

  while (queue.length) {
    let minIdx = 0;
    for (let i = 1; i < queue.length; i++) {
      if ((dist[queue[i]] ?? Infinity) < (dist[queue[minIdx]] ?? Infinity)) minIdx = i;
    }
    const curKey = queue.splice(minIdx, 1)[0];
    if (settled.has(curKey)) continue;
    settled.add(curKey);

    const sep = curKey.lastIndexOf("|");
    const station = curKey.slice(0, sep);
    const group = Number(curKey.slice(sep + 1));

    for (const edge of adjacency[station] || []) {
      const penalty = group !== -1 && group !== edge.group ? TRANSFER_PENALTY : 0;
      const nd = dist[curKey] + edge.dist + penalty;
      const nk = keyOf(edge.to, edge.group);
      if (nd < (dist[nk] ?? Infinity)) {
        dist[nk] = nd;
        prev[nk] = { fromKey: curKey, from: station, ...edge };
        queue.push(nk);
      }
    }
  }

  // Best state reaching the goal.
  let goalKey = null;
  let goalDist = Infinity;
  for (const k in dist) {
    if (k.slice(0, k.lastIndexOf("|")) === goal && dist[k] < goalDist) {
      goalDist = dist[k];
      goalKey = k;
    }
  }
  if (goalKey === null) return null;

  const path = [goal];
  const edges = [];
  let cur = goalKey;
  while (cur !== startKey) {
    const p = prev[cur];
    if (!p) return null;
    edges.push(p);
    path.push(p.from);
    cur = p.fromKey;
  }
  path.reverse();
  edges.reverse();

  return { path, edges };
}

// Walk the edges and collect the polyline for each hop, merging consecutive
// hops that stay on the same logical line into one coloured segment.
function buildSegments(lines, edges) {
  const segments = [];

  for (const edge of edges) {
    const line = lines[edge.lineIdx];
    const step = edge.fromIdx <= edge.toIdx ? 1 : -1;
    const pts = [];
    for (let j = edge.fromIdx; j !== edge.toIdx + step; j += step) {
      pts.push([line.points[j].x, line.points[j].y]);
    }

    const last = segments[segments.length - 1];
    if (last && last.group === edge.group) {
      const lastPt = last.points[last.points.length - 1];
      const firstPt = pts[0];
      if (lastPt[0] === firstPt[0] && lastPt[1] === firstPt[1]) {
        last.points.push(...pts.slice(1));
      } else {
        last.points.push(...pts);
      }
    } else {
      segments.push({ group: edge.group, color: line.color, points: pts });
    }
  }

  return segments.map(({ color, points }) => ({ color, points }));
}

export function findRoute(tramData, fromName, toName) {
  const from = normalize(fromName);
  const to = normalize(toName);
  if (!from || !to || from === to) return null;

  const { lines, adjacency } = buildGeometry(tramData);
  const result = dijkstra(adjacency, from, to);
  if (!result) return null;

  return {
    path: result.path,
    segments: buildSegments(lines, result.edges),
  };
}
